import { MESSAGES } from "../helpers/message-helper";

function getItemRestId() {
  if (Office.context.mailbox.diagnostics.hostName === "OutlookIOS") {
    // itemId is already REST-formatted.
    return Office.context.mailbox.item.itemId;
  } else {
    // Convert to an item ID for API v2.0.
    return Office.context.mailbox.convertToRestId(
      Office.context.mailbox.item.itemId,
      Office.MailboxEnums.RestVersion.v2_0
    );
  }
}

export function getConversationId(item) {
  const conversationId = item.conversationId.replaceAll("+", "_");
  return conversationId;
}

export function getProviderEmailAddress(item) {
  const fallbackEmailAddress = Office.context.mailbox.userProfile.emailAddress;

  if (!item.getSharedPropertiesAsync) {
    return Promise.resolve(fallbackEmailAddress);
  }

  return new Promise((resolve) => {
    item.getSharedPropertiesAsync((result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded && result.value?.owner) {
        resolve(result.value.owner);
        return;
      }

      resolve(fallbackEmailAddress);
    });
  });
}

function isProviderMessage(message, providerEmailAddress) {
  const normalizedProviderEmailAddress = providerEmailAddress.toLowerCase();
  const senderEmailAddress = message.sender?.emailAddress?.address?.toLowerCase();
  const fromEmailAddress = message.from?.emailAddress?.address?.toLowerCase();

  return senderEmailAddress === normalizedProviderEmailAddress || fromEmailAddress === normalizedProviderEmailAddress;
}

export async function generateRequestBody(messagesList, providerEmailAddress) {
  const item = Office.context.mailbox.item;
  const itemId = getItemRestId();
  const conversationId = getConversationId(item);

  const requestBody = {
    conversation_id: `outlook:${conversationId}`,
    communication_channel: "outlook",
    guest_email_address: item.from.emailAddress,
    guest_name: item.from.displayName,
    provider_email_address: providerEmailAddress,
    email_subject: item.subject,
    email_body: "",
    email_thread_history: [],
  };

  (messagesList || []).some((message) => {
    // messages are in ascending order...

    const messageBody = message.uniqueBody?.content || "";

    // loop into the messages list until we find the selected messages..
    if (message.id === itemId) {
      requestBody.email_body = messageBody;
      return true;
    }

    // push message body into thread history and loop again to find selected message
    const type = isProviderMessage(message, requestBody.provider_email_address) ? "provider" : "guest";
    requestBody.email_thread_history.push({ [type]: messageBody });

    return false;
  });

  // Fallback: if the messagesList loop didn't populate email_body (Graph returned no matching
  // message, ID format mismatch, or the user is on a host where /users/{owner}/messages is
  // unavailable), pull the current message body directly from Office.js so the AI always
  // has at least the selected message to work with.
  // Logged by ai-services, so we can see how often drafts miss the Graph thread ("onprem" is set by
  // commands.js for on-prem installs).
  requestBody.thread_source = requestBody.email_body ? "graph" : "open_email";

  if (!requestBody.email_body) {
    requestBody.email_body = await getCurrentItemBody(item);
  }

  return { ...requestBody };
}

function getCurrentItemBody(item) {
  if (!item.body || typeof item.body.getAsync !== "function") {
    return Promise.resolve("");
  }
  return new Promise((resolve) => {
    item.body.getAsync(Office.CoercionType.Text, (asyncResult) => {
      if (asyncResult.status === Office.AsyncResultStatus.Succeeded && asyncResult.value) {
        resolve(asyncResult.value);
      } else {
        resolve("");
      }
    });
  });
}

export function validateAIResponse(res) {
  let isValid = true;
  let errorMessage = "";

  if (res?.status && res.status === "error") {
    errorMessage = MESSAGES.defaultError;
    isValid = false;

    if (res?.error_code === "HOTEL_NOT_CONFIGURED") {
      errorMessage = MESSAGES.hotelNotConfigured;
    }

    if (res?.error_code === "HOTEL_DISABLED") {
      errorMessage = MESSAGES.hotelDisabled;
    }

    if (res?.error_message) {
      errorMessage = res?.error_message;
    }
  }

  return { isValid, errorMessage };
}
